<template>
  <div v-if="!store.hasError" class="fixed bottom-0 w-full !bg-white !bg-opacity-90 !backdrop-blur-lg lg:hidden">
    <div class="mobile-buttons-wrapper">
      <div
        id="mobile-buttons"
        ref="buttonsContainer"
        class="flex my-2"
        @scroll="checkScrollEnd"
        :class="{
          'blur-hidden': scrollHasReachedEnd || !scrollVisible,
          'mobile-buttons-scroll-disabled': dropdownActive,
        }"
      >
        <div class="flex justify-center">
          <DownloadMenu
            v-if="!isProcessing"
            :is-processing="isProcessing"
            :is-processing-hd-download="store.isProcessingHdDownload"
            :is-processing-preview-download="store.isProcessingPreviewDownload"
            :is-hd-image-processed="store.selectedImage.hdResult.state === ProcessingState.Finished"
            :is-generating-background="props.currentPersistentStore?.generatingBackground"
            :is-processing-magic-background-hd-download="store.isProcessingMagicBackgroundHdDownload"
            :is-mobile="true"
            :is-bottom-bar="true"
            :persistent-store="props.currentPersistentStore"
            :preview-resolution="previewResolution"
            :full-resolution="fullResolution"
            :hd-resolution="hdResolution"
            :download-preview="props.downloadPreview"
            :download-hd="props.downloadHd"
            :hd-download-enabled="hdDownloadEnabled"
            :download-magic-background-hd="props.downloadMagicBackgroundHd"
            v-model:dropdown-active="dropdownActive"
          />
          <div class="flex gap-1 items-start justify-center cursor-wait" v-else>
            <div class="h-11 w-11 rounded-full !bg-secondary animate-pulse"></div>
          </div>
        </div>

        <!-- Add Background Button-->
        <div v-if="props.isProcessing" class="flex gap-1 items-start justify-center cursor-wait">
          <div class="h-11 w-11 rounded-full !bg-secondary animate-pulse"></div>
        </div>
        <ActionButton v-else class="small-tablet-flex-col" @click="props.togglePanel('addBackground')">
          <template #icon>
            <NewBackgroundIcon
              v-if="
                !props.currentPersistentStore?.selectedBackgroundPhotoUrl &&
                !props.currentPersistentStore?.selectedBackgroundColor
              "
            />
            <span v-else class="w-full h-full" :style="props.backgroundActionButtonStyle" />
          </template>
          <template #title
            ><span class="pt-1">{{ I18n.t(`editor.background`) }}</span></template
          >
        </ActionButton>

        <!-- Brush Button -->
        <div v-if="props.isProcessing" class="flex gap-1 items-start justify-center cursor-wait">
          <div class="h-11 w-11 rounded-full !bg-secondary animate-pulse"></div>
        </div>
        <span
          v-else-if="hasExceededMagicBrushIterations(store.selectedImage, props.refreshTippy)"
          class="hover-tooltip flex justify-center"
          :data-tippy-content="`${I18n.t('ai_brush.error.iterations_exceeded')}`"
          data-tippy-placement="bottom"
        >
          <ActionButton
            class="small-tablet-flex-col"
            :disabled="hasExceededMagicBrushIterations(store.selectedImage, props.refreshTippy)"
          >
            <template #icon>
              <NewBrushIcon />
            </template>
            <template #title
              ><span class="pt-1">{{ I18n.t(`editor.cutout`) }}</span></template
            >
          </ActionButton>
        </span>

        <template v-else>
          <ActionButton class="small-tablet-flex-col" @click="props.togglePanel('eraseRestore')">
            <template #icon>
              <NewBrushIcon />
            </template>
            <template #title
              ><span class="pt-1">{{ I18n.t(`editor.cutout`) }}</span></template
            >
          </ActionButton>
        </template>

        <!-- Adjust Button -->
        <template>
          <div v-if="props.isProcessing" class="flex gap-1 items-start justify-center cursor-wait">
            <div class="h-11 w-11 rounded-full !bg-secondary animate-pulse"></div>
          </div>
          <ActionButton v-else class="small-tablet-flex-col" @click="props.togglePanel('adjust')">
            <template #icon>
              <AdjustIcon />
            </template>
            <template #title
              ><span class="pt-1">{{ I18n.t(`editor.adjust`) }}</span></template
            >
          </ActionButton>
        </template>

        <!-- Effects button -->
        <template>
          <div v-if="props.isProcessing" class="flex gap-1 items-start justify-center cursor-wait">
            <div class="h-11 w-11 rounded-full !bg-secondary animate-pulse test"></div>
          </div>
          <ActionButton v-else class="small-tablet-flex-col" @click="props.togglePanel('fx')">
            <template #icon>
              <ShadowIcon />
            </template>
            <template #title
              ><span class="pt-1">{{ I18n.t(`editor.apply_effects`) }}</span></template
            >
          </ActionButton>
        </template>

        <!-- Design Button -->
        <template>
          <div v-if="props.isProcessing" class="flex gap-1 items-start justify-center cursor-wait">
            <div class="h-11 w-11 rounded-full !bg-secondary animate-pulse"></div>
          </div>
          <ActionButton v-else class="small-tablet-flex-col" @click="props.togglePanel('design')">
            <template #icon>
              <DesignIcon />
            </template>
            <template #title
              ><span class="pt-1">{{ I18n.t(`editor.design`) }}</span></template
            >
          </ActionButton>
        </template>
      </div>
      <button
        class="scroll-indicator"
        :class="{ visible: !scrollHasReachedEnd && scrollVisible }"
        @click="scrollToEnd"
        type="button"
        aria-label="Scroll to end"
      >
        <ChevronRightIcon class="text-secondary-active" />
      </button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ProcessingState, Image, hasExceededMagicBrushIterations } from "@/modules/internal_api/image";
import { Panel, useEditorStore } from "@/stores/editor_store";
import { PersistentStore } from "@/stores/persistent_store";
import { StyleValue, computed, inject, onMounted, onUnmounted, ref, watch, nextTick } from "vue";
import {
  ActionButton,
  NewBrushIcon,
  NewBackgroundIcon,
  ShadowIcon,
  AdjustIcon,
  DesignIcon,
  ChevronRightIcon,
} from "prism";
import { Stage } from "konva/lib/Stage";
import DownloadMenu from "./download_controls/download_menu.vue";

interface Props {
  stage: Stage;
  stageHd: Stage;
  credits: number;
  editor: HTMLElement;
  isProcessing: boolean;

  currentPersistentStore: PersistentStore;
  backgroundActionButtonStyle: StyleValue;
  downloadPreview: (force_share?: boolean) => Promise<void>;
  downloadHd: (closeDialog?: () => void) => Promise<void>;
  loadCreditsAndUpdatePreview: () => Promise<void>;
  refreshTippy: () => void;
  togglePanel: (id: Panel) => Promise<void>;
  getDataURL: (image: Image, stage: any, element: any) => string;
  resetZoom: () => void;
  refreshAllHdLayers: (image: Image) => Promise<void>;
  downloadMagicBackgroundHd: (generationId: string, closeDialog?: () => void) => Promise<void>;
}

const I18n = inject("I18n");
const props = defineProps<Props>();
const store = useEditorStore();

const scrollHasReachedEnd = ref(false);
const scrollVisible = ref(false);
const buttonsContainer = ref<HTMLDivElement>();
const dropdownActive = ref(false);

const checkScrollEnd = (event: Event): void => {
  const { scrollWidth, scrollLeft, clientWidth } = event.target as HTMLElement;
  const tolerance = 10;
  scrollHasReachedEnd.value = scrollWidth - scrollLeft - clientWidth <= tolerance;
};

const hasHorizontalScroll = () => {
  if (buttonsContainer.value) {
    return buttonsContainer.value.scrollWidth > buttonsContainer.value.clientWidth;
  }
  return false;
};

const checkScroll = () => {
  // Trigger Vue's reactivity system by accessing the computed property
  scrollVisible.value = hasHorizontalScroll();
};

const scrollToEnd = () => {
  if (buttonsContainer.value) {
    buttonsContainer.value.scrollTo({
      left: buttonsContainer.value.scrollWidth,
      behavior: "smooth",
    });
  }
};

watch([() => props.isProcessing], () => {
  // Scroll reset when there is image uploaded
  if (buttonsContainer.value) {
    buttonsContainer.value.scrollLeft = 0;
  }
});

const previewResolution = computed(() => {
  return `${store.selectedImage.meta.previewWidth} x ${store.selectedImage.meta.previewHeight}`;
});

const hdResolution = computed(() => {
  return `${store.selectedImage.meta.hdWidth} x ${store.selectedImage.meta.hdHeight}`;
});

const fullResolution = computed(() => {
  return `${store.selectedImage.meta.fullWidth} x ${store.selectedImage.meta.fullHeight}`;
});

const hdDownloadEnabled = computed(() => {
  return store.selectedImage.meta.fullAvailable;
});

onMounted(async () => {
  window.addEventListener("resize", checkScroll);
  checkScroll();

  // Ensure scroll starts at beginning
  await nextTick();
  if (buttonsContainer.value) {
    buttonsContainer.value.scrollLeft = 0;
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", checkScroll);
});
</script>
<style lang="scss">
#mobile-buttons {
  align-items: center;
  justify-content: center;
  padding: 5px 0;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  & > * {
    min-width: 72px;
    flex-shrink: 0;
  }

  .btn-hd-download {
    background: rgb(234 241 255 / var(--tw-bg-opacity));
    color: rgb(25 93 243 / var(--tw-bg-opacity));

    &:active {
      background: rgb(173 199 255 / var(--tw-bg-opacity));
    }

    &:hover {
      background: rgb(193 213 255 / var(--tw-bg-opacity));
      scale: 1.08;
      transition: scale ease-in-out 0.2s;
    }
  }

  @media (min-width: 480px) {
    & {
      gap: 1rem;
      justify-content: center;
    }
  }

  @media (max-width: 479px) {
    & {
      overflow-x: scroll;
      align-items: flex-start;
    }

    &::after,
    &::before {
      position: absolute;
      height: 100%;
      width: 3rem;
      content: "";
      top: 0;
      right: 0;
      pointer-events: auto;
      opacity: 1;
      background: linear-gradient(270deg, #fff 0%, rgba(255, 255, 255, 0.75) 70%, rgba(255, 255, 255, 0) 100%);
      transition: opacity 150ms;
      transition-timing-function: cubic-bezier(0.1, 0.7, 1, 0.1);
    }
  }

  @media (min-width: 450px) {
    & {
      justify-content: center;
    }
  }

  @media (max-width: 450px) {
    & {
      justify-content: flex-start;
    }
  }

  @media (max-width: 570px) {
    & {
      gap: 0.25rem;
    }
  }

  @media (max-width: 375px) {
    & {
      gap: 0;
    }
  }
}

.mobile-buttons-scroll-disabled {
  overflow-x: visible !important;
}

.mobile-buttons-wrapper {
  position: relative;
}

.scroll-indicator {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  font-weight: bold;
  position: absolute;
  top: -0.5rem;
  right: 0.5rem;
  opacity: 0;
  transition: opacity 150ms;
  transition-timing-function: cubic-bezier(0.1, 0.7, 1, 0.1);
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;

  &:active {
    transform: scale(0.95);
  }

  svg {
    height: 20px;
  }
}

.blur-hidden::after,
.blur-hidden::before {
  opacity: 0 !important;
  pointer-events: none !important;
}
@media (max-width: 479px) {
  .visible {
    opacity: 1;
  }
}

.small-tablet-flex-col {
  @media (max-width: 1024px) {
    flex-direction: column !important;
  }
}
</style>
